import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Warning, ArrowsClockwise, Check, X, Flag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { memoryApi } from "@/api";

export default function Consistency() {
  const { pid } = useParams();
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);

  const load = () => memoryApi.inconsistencies(pid).then(setItems).catch(() => {});
  useEffect(() => { load(); }, [pid]);

  const run = async () => {
    setRunning(true);
    try {
      const res = await memoryApi.runConsistency(pid);
      setItems(res.inconsistencies);
      toast.success(`${res.count} incohérence(s) possible(s)`);
    } finally { setRunning(false); }
  };

  const act = async (id, action) => {
    await memoryApi.incAction(pid, id, { action });
    toast.success(action === "accept" ? "Confirmée" : action === "volontaire" ? "Marquée volontaire" : "Ignorée");
    load();
  };

  const statusStyle = { pending: "border-incoherence", "confirmée": "border-erreur", "ignorée": "border-border opacity-60", "volontaire": "border-emerald-500" };

  return (
    <div className="p-8 max-w-4xl fade-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="overline text-primary mb-1">Détection de cohérence</p>
          <h1 className="font-serif text-4xl font-light tracking-tight">Incohérences possibles</h1>
        </div>
        <Button onClick={run} disabled={running} className="rounded-sm gap-2" data-testid="run-consistency">
          <ArrowsClockwise size={16} className={running ? "animate-spin" : ""} /> Analyser la cohérence globale
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-16 text-center text-muted-foreground">
          <Warning size={40} weight="duotone" className="mx-auto mb-3 opacity-50" />
          Aucune incohérence détectée. Lancez l'analyse de cohérence globale après avoir analysé les chapitres.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`inconsistency-${it.id}`} className={`border-l-4 border border-border rounded-sm bg-surface p-5 ${statusStyle[it.status] || ""}`}>
              <div className="flex items-start gap-3">
                <Warning size={22} weight="duotone" style={{ color: "#4F46E5" }} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium mb-2">⚠️ {it.title}</h3>
                  <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-sans mb-3">{it.description}</pre>
                  {it.status === "pending" ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => act(it.id, "accept")} className="rounded-sm gap-1" data-testid={`inc-confirm-${it.id}`}><Check size={14} /> Confirmer l'erreur</Button>
                      <Button size="sm" variant="outline" onClick={() => act(it.id, "volontaire")} className="rounded-sm gap-1" data-testid={`inc-volontaire-${it.id}`}><Flag size={14} /> Volontaire</Button>
                      <Button size="sm" variant="ghost" onClick={() => act(it.id, "refuse")} className="rounded-sm gap-1" data-testid={`inc-ignore-${it.id}`}><X size={14} /> Ignorer</Button>
                    </div>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-sm bg-secondary text-secondary-foreground capitalize">{it.status}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
